'use client';

import { EditorContent, EditorContext, useEditor } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';

// --- Tiptap Core Extensions ---
import { TaskItem, TaskList } from '@tiptap/extension-list';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Typography } from '@tiptap/extension-typography';
import { Selection } from '@tiptap/extensions';
import { StarterKit } from '@tiptap/starter-kit';

// --- UI Primitives ---
import { Button } from '@src/components/tiptap-ui-primitive/button';
import { Spacer } from '@src/components/tiptap-ui-primitive/spacer';
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from '@src/components/tiptap-ui-primitive/toolbar';

// --- Tiptap Node ---
import '@src/components/tiptap-node/blockquote-node/blockquote-node.scss';
import '@src/components/tiptap-node/code-block-node/code-block-node.scss';
import '@src/components/tiptap-node/heading-node/heading-node.scss';
import { HorizontalRule } from '@src/components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension';
import '@src/components/tiptap-node/horizontal-rule-node/horizontal-rule-node.scss';
import '@src/components/tiptap-node/list-node/list-node.scss';
import '@src/components/tiptap-node/paragraph-node/paragraph-node.scss';

// --- Tiptap UI ---
import { BlockquoteButton } from '@src/components/tiptap-ui/blockquote-button';
import { CodeBlockButton } from '@src/components/tiptap-ui/code-block-button';
import { HeadingDropdownMenu } from '@src/components/tiptap-ui/heading-dropdown-menu';
import {
  LinkButton,
  LinkContent,
  LinkPopover,
} from '@src/components/tiptap-ui/link-popover';
import { ListDropdownMenu } from '@src/components/tiptap-ui/list-dropdown-menu';
import { MarkButton } from '@src/components/tiptap-ui/mark-button';
import { TextAlignButton } from '@src/components/tiptap-ui/text-align-button';
import { UndoRedoButton } from '@src/components/tiptap-ui/undo-redo-button';

// --- Icons ---
import { ArrowLeftIcon } from '@src/components/tiptap-icons/arrow-left-icon';
import { LinkIcon } from '@src/components/tiptap-icons/link-icon';

// --- Hooks ---
import { useIsBreakpoint } from '@src/hooks/use-is-breakpoint';

// --- Components ---
import { ThemeToggle } from './theme-toggle';

// --- Lib ---

// --- Styles ---
import './simple-editor.scss';

import { cn } from '@repo/ui/utils';

const MainToolbarContent = ({
  onLinkClick,
  isMobile,
  showUndoRedo,
  showSuperScript,
  darkModeSwitch,
}: {
  onLinkClick: () => void;
  isMobile: boolean;
  showUndoRedo?: boolean;
  showSuperScript?: boolean;
  darkModeSwitch?: boolean;
}) => {
  return (
    <>
      <Spacer />

      {showUndoRedo && (
        <ToolbarGroup>
          <UndoRedoButton action="undo" />
          <UndoRedoButton action="redo" />
        </ToolbarGroup>
      )}

      <ToolbarSeparator />

      <ToolbarGroup>
        <HeadingDropdownMenu levels={[1, 2, 3, 4]} portal={isMobile} />
        <ListDropdownMenu
          types={['bulletList', 'orderedList', 'taskList']}
          portal={isMobile}
        />
        <BlockquoteButton />
        <CodeBlockButton />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="strike" />
        <MarkButton type="code" />
        <MarkButton type="underline" />
        {!isMobile ? <LinkPopover /> : <LinkButton onClick={onLinkClick} />}
      </ToolbarGroup>

      {showSuperScript && (
        <>
          <ToolbarSeparator />
          <ToolbarGroup>
            <MarkButton type="superscript" />
            <MarkButton type="subscript" />
          </ToolbarGroup>
        </>
      )}

      <ToolbarSeparator />

      <ToolbarGroup>
        <TextAlignButton align="left" />
        <TextAlignButton align="center" />
        <TextAlignButton align="right" />
        <TextAlignButton align="justify" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <Spacer />

      {isMobile && <ToolbarSeparator />}

      {darkModeSwitch && (
        <ToolbarGroup>
          <ThemeToggle />
        </ToolbarGroup>
      )}
    </>
  );
};

const MobileToolbarContent = ({ onBack }: { onBack: () => void }) => (
  <>
    <ToolbarGroup>
      <Button data-style="ghost" onClick={onBack}>
        <ArrowLeftIcon className="tiptap-button-icon" />
        <LinkIcon className="tiptap-button-icon" />
      </Button>
    </ToolbarGroup>

    <ToolbarSeparator />

    <LinkContent />
  </>
);

export interface SimpleEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  darkModeSwitch?: boolean;
  readOnly?: boolean;
  className?: string;
}

export function SimpleEditor({
  value,
  onChange,
  darkModeSwitch,
  readOnly,
  className,
}: SimpleEditorProps) {
  const isMobile = useIsBreakpoint();
  const [mobileView, setMobileView] = useState<'main' | 'link'>('main');
  const toolbarRef = useRef<HTMLDivElement>(null);

  const editor = useEditor({
    immediatelyRender: false,
    editable: !readOnly,
    editorProps: {
      attributes: {
        autocomplete: 'off',
        autocorrect: 'off',
        autocapitalize: 'off',
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': 'Main content area, start typing to enter text.',
        class: cn('simple-editor', readOnly && 'read-only'),
      },
    },
    onUpdate: (event) => {
      onChange?.(event.editor.getHTML());
    },
    extensions: [
      StarterKit.configure({
        horizontalRule: false,
        link: {
          openOnClick: false,
          enableClickSelection: true,
        },
      }),
      HorizontalRule,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Typography,
      TextStyle,
      Superscript,
      Subscript,
      Selection,
    ],
    content: value,
  });

  useEffect(() => {
    if (!isMobile && mobileView !== 'main') {
      setMobileView('main');
    }
  }, [isMobile, mobileView]);

  return (
    <div
      className={cn(
        'simple-editor-wrapper relative',
        readOnly && 'read-only',
        className
      )}
    >
      <EditorContext.Provider value={{ editor }}>
        {!readOnly && (
          <Toolbar ref={toolbarRef}>
            {mobileView === 'main' ? (
              <MainToolbarContent
                onLinkClick={() => setMobileView('link')}
                isMobile={isMobile}
                darkModeSwitch={darkModeSwitch}
              />
            ) : (
              <MobileToolbarContent onBack={() => setMobileView('main')} />
            )}
          </Toolbar>
        )}
        <EditorContent
          editor={editor}
          role="presentation"
          className="simple-editor-content"
        />
      </EditorContext.Provider>
    </div>
  );
}
